import {
  DSBadge,
  DSButton,
  DSCardWrapper,
} from "@components/design-system/molecules";
import {
  faCheck,
  faShieldHalved,
  faSpinner,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCompanionV2 } from "@hooks/use-companion-v2";
import type {
  CompanionV2Message,
  ToolPermissionStatus,
} from "@hooks/use-companion-v2";

const STATUS_BADGE: Record<
  Exclude<ToolPermissionStatus, "pending">,
  { label: string; variant: "green" | "red" | "yellow" }
> = {
  approved: { label: "Allowed", variant: "green" },
  rejected: { label: "Denied", variant: "red" },
  expired: { label: "Expired", variant: "yellow" },
};

export const ToolPermissionCard = ({
  message,
}: {
  message: CompanionV2Message;
}) => {
  const { decideToolPermission } = useCompanionV2();
  const permission = message.toolPermission;
  if (!permission) return null;

  const isPending = permission.status === "pending";
  const isResolving = permission.isResolving ?? false;
  const badge = isPending
    ? null
    : STATUS_BADGE[
        permission.status as Exclude<ToolPermissionStatus, "pending">
      ];

  return (
    <div className="w-full px-sm my-xs">
      <DSCardWrapper
        variant="info"
        dataTestId="companion-tool-permission-card"
      >
        <div className="flex flex-col gap-sm">
          <div className="flex items-start gap-sm">
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-subtle text-brand">
              <FontAwesomeIcon
                icon={faShieldHalved}
                className="text-sm"
              />
            </span>
            <div className="min-w-0 flex flex-col gap-xs">
              <div className="text-sm font-semibold text-primary">
                {permission.title}
              </div>
              <div className="text-xs text-secondary">
                {permission.summary}
              </div>
            </div>
          </div>

          {permission.details.length > 0 && (
            <dl className="grid grid-cols-[auto_1fr] gap-x-md gap-y-xs rounded-medium bg-primary/40 px-sm py-xs text-xs text-secondary">
              {permission.details.map((detail) => (
                <div
                  key={`${detail.label}:${detail.value}`}
                  className="contents"
                >
                  <dt className="font-medium text-primary">
                    {detail.label}
                  </dt>
                  <dd className="min-w-0 truncate">{detail.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {permission.error && (
            <div className="text-xs text-danger">
              {permission.error}
            </div>
          )}

          {isPending ? (
            <div className="flex flex-wrap items-center gap-sm">
              <DSButton
                variant="primary"
                size="sm"
                disabled={isResolving}
                onClick={() =>
                  decideToolPermission(message.id, "approve")
                }
                dataTestId="companion-tool-permission-allow"
              >
                <span className="inline-flex items-center gap-xs">
                  <FontAwesomeIcon
                    icon={isResolving ? faSpinner : faCheck}
                    className={isResolving ? "animate-spin" : ""}
                  />
                  Allow
                </span>
              </DSButton>
              <DSButton
                variant="danger"
                size="sm"
                disabled={isResolving}
                onClick={() =>
                  decideToolPermission(message.id, "reject")
                }
                dataTestId="companion-tool-permission-deny"
              >
                <span className="inline-flex items-center gap-xs">
                  <FontAwesomeIcon icon={faXmark} />
                  Deny
                </span>
              </DSButton>
              {permission.canPersist && (
                <DSButton
                  variant="secondary"
                  size="sm"
                  disabled={isResolving}
                  onClick={() =>
                    decideToolPermission(message.id, "approve", true)
                  }
                  dataTestId="companion-tool-permission-always-allow"
                >
                  <span className="inline-flex items-center gap-xs">
                    <FontAwesomeIcon
                      icon={isResolving ? faSpinner : faCheck}
                      className={isResolving ? "animate-spin" : ""}
                    />
                    {permission.persistLabel}
                  </span>
                </DSButton>
              )}
            </div>
          ) : badge ? (
            <DSBadge
              size="sm"
              variant={badge.variant}
              dataTestId="companion-tool-permission-status"
            >
              {badge.label}
            </DSBadge>
          ) : null}
        </div>
      </DSCardWrapper>
    </div>
  );
};
